import React, { useContext, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { Dispatch, bindActionCreators } from "redux";

import { Dialog, CancelLink, Button } from "@plex/react-components";

import { commonMessages } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { DialogContainer, DialogNavigationContext } from "ui/app/navigation";
import { Upload } from "ui/components";
import { useLocalizedValues } from "ui/hooks";
import commonStyles from "ui/screens/translation-managers/shared/styles/Translations.module.scss";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { translationUploadActions } from "./TranslationUpload.actions";
import { ITranslationUploadState, ITranslationUploadError } from "./TranslationUpload.state";

export interface ITranslationUploadProps {
  onSubmit: (file: File[]) => void;
  error?: ITranslationUploadError;
  uploadProcessing: boolean;
  uploadCompleted: boolean;
  notifications: INotification[];
  resetState: () => void;
  onUploadCompleted: (error?: ITranslationUploadError) => void;
  progress: number;
}

const TranslationUpload: React.FC<ITranslationUploadProps> = ({ error, onSubmit, uploadProcessing, uploadCompleted, notifications, resetState, onUploadCompleted, progress }) => {
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  const dialogNavigationContext = useContext(DialogNavigationContext);

  const localizedValues = useLocalizedValues(() => ({
    dialogTitle: displayMessages.title("selectTranslationsUploadFile"),
    submit: commonMessages.action("submit")
  }));

  const updateAttachedFilesList = useCallback(
    (files: File[]) => {
      setAttachedFiles(files);

      resetState();
    },
    [resetState]
  );

  const onHidden = useCallback(() => {
    if (!error) {
      resetState();
    }
  }, [error, resetState]);

  const onSubmitExecute = useCallback(() => {
    onSubmit(attachedFiles);
  }, [onSubmit, attachedFiles]);

  useEffect(() => {
    if (uploadCompleted) {
      dialogNavigationContext.hideDialog(undefined, onHidden);

      onUploadCompleted(error);
    }
  }, [uploadCompleted, onUploadCompleted, error, dialogNavigationContext, onHidden]);

  return (
    <DialogContainer
      onHidden={onHidden}
      render={dialogProps => {
        return (
          <Dialog {...dialogProps} title={localizedValues.dialogTitle}>
            <Dialog.Body>
              <Banner messages={notifications} messageClassName={commonStyles.dialogBanner} />
              <Upload onFilesAttached={updateAttachedFilesList} uploadingProcessing={uploadProcessing} progress={progress} accept={[".xml"]} id="termTranslationUploader" />
            </Dialog.Body>
            <Dialog.Footer>
              <CancelLink onClick={dialogProps.onHide} />
              <Button onClick={onSubmitExecute}>{localizedValues.submit}</Button>
            </Dialog.Footer>
          </Dialog>
        );
      }}
    />
  );
};

export default connect(
  (state: { translationUpload: ITranslationUploadState }) => {
    return {
      error: state.translationUpload.error,
      uploadProcessing: state.translationUpload.uploadProcessing,
      uploadCompleted: state.translationUpload.uploadCompleted,
      notifications: state.translationUpload.notifications,
      progress: state.translationUpload.progress
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        onSubmit: translationUploadActions.uploadTranslation,
        resetState: translationUploadActions.resetUploadingTranslation
      },
      dispatch
    );
  }
)(TranslationUpload);
